<template>
  <div class="flex gap-4 border-none p-0 w-full overflow-hidden">
    <!-- Drag and Drop Upload Box -->
    <div
      v-if="!isLocalized"
      :disabled="!canSelectMore"
      class="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer transition-colors duration-300 min-w-[11rem] w-[11rem] h-[8.25rem] flex justify-center items-center flex-shrink-0 mt-[1rem]"
      :class="{
        'bg-gray-100': isDragging,
        'opacity-50 cursor-not-allowed': !canSelectMore
      }"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="handleDragLeave"
    >
      <div class="flex flex-col items-center">
        <i class="mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
        </i>
        <p>{{ $t("common.drag_and_drop") }}</p>
        <p>
          {{ $t("common.or") }}
          <label
            for="file-input"
            class="bg-transparent font-bold cursor-pointer text-[rgba(121,128,244,1)] rounded-xl underline"
          >
            {{ $t("common.browse") }}
          </label>
        </p>
        <input
          :disabled="!canSelectMore"
          id="file-input"
          type="file"
          multiple
          @change="handleFileSelection"
          accept="image/*"
          class="hidden"
        />
      </div>
    </div>

    <!-- Uploaded Images Slider -->
    <div v-if="allFiles.length" class="relative w-full overflow-hidden">
      <!-- Arrow -->
      <button
        v-if="canShowArrow"
        @click="scrollNext"
        :class="[
          'absolute top-1/2 -translate-y-1/2 z-10 bg-[rgba(121,128,244,1)] hover:bg-[rgba(100,108,255,1)] rounded-full p-2 text-white shadow-md transition-transform',
          isRTL ? 'end-0 rotate-180' : 'end-0'
        ]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      <!-- Scrollable Image Row -->
      <div
        ref="scrollContainer"
        :dir="isRTL ? 'rtl' : 'ltr'"
        :class="[
          'flex gap-4 scroll-smooth px-2 pt-[1rem] scrollbar-none overflow-x-auto',
          isRTL
            ? canScroll
              ? 'flex-row-reverse w-full'
              : 'flex-row-reverse w-fit'
            : 'flex-row w-full max-w-full'
        ]"
        @scroll="checkScroll"
      >
        <div
          v-for="(file, index) in allFiles"
          :key="index"
          class="relative flex-shrink-0"
        >
          <img
            :src="file.preview"
            alt="image"
            class="h-[8.25rem] w-[11rem] object-cover rounded-xl border border-gray-300"
          />

          <!-- Remove -->
          <button
            v-if="!isLocalized"
            @click="removeFile(index)"
            class="absolute -top-3 -right-3 w-[2rem] h-[2rem] flex items-center justify-center"
          >
            <img
              class="w-[2rem] h-[2rem]"
              src="@/assets/icons/Exam-image-close.svg"
              alt="x icon"
            />
          </button>

          <!-- Replace -->
          <button
            v-if="isLocalized"
            @click="replaceFile(index)"
            class="absolute bottom-[0.5rem] right-[0.5rem] w-[2.5rem] h-[2.5rem]"
          >
            <div
              class="flex justify-center items-center w-[2.5rem] h-[2.5rem] rounded-full bg-[rgba(0,0,0,0.7)]"
            >
              <img
                class="w-[1.375rem] h-[1.375rem]"
                src="@/assets/icons/replace-image.svg"
                alt="replace icon"
              />
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, computed, onMounted, watch } from "vue";
import MediaService from "@/services/mediaService";
import { useToast } from "vue-toastification";

interface FileWithPreview {
  file: File | null;
  preview: string;
  uploaded: boolean;
  order?: number;
}

export default defineComponent({
  name: "MediaUploader",
  props: {
    mediaLabel: { type: String, required: true },
    questionType: { type: String, default: "" },
    isLocalized: { type: Boolean, default: false },
    mediaUrls: { type: Array as () => { path: string; order?: number }[], default: () => [] },
  },
  emits: ["media-uploaded", "media-removed"],
  setup(props, { emit }) {
    const allFiles = ref<FileWithPreview[]>([]);
    const isDragging = ref(false);
    const scrollContainer = ref<HTMLElement | null>(null);
    const canScroll = ref(false);
    const toast = useToast();

    const isRTL = computed(() => document.documentElement.getAttribute("dir") === "rtl");

    const maxImages = computed(() => {
      const type = props.questionType?.toUpperCase();
      return type === "ESSAY" || type === "AUDIO_RESPONSE" ? 4 : 1;
    });

    const canSelectMore = computed(() => allFiles.value.length < maxImages.value);
    const canShowArrow = computed(() => canScroll.value && allFiles.value.length > 1);

    function checkScroll() {
      const el = scrollContainer.value;
      if (!el) return;
      canScroll.value = el.scrollWidth > el.clientWidth + 10;
    }

    function scrollNext() {
      const el = scrollContainer.value;
      if (!el) return;
      const scrollAmount = 200;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (isRTL.value) {
        if (el.scrollLeft <= 0) el.scrollTo({ left: maxScroll, behavior: "smooth" });
        else el.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
        if (isAtEnd) el.scrollTo({ left: 0, behavior: "smooth" });
        else el.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }

    async function handleFileSelection(event: Event) {
      const target = event.target as HTMLInputElement;
      if (!target.files) return;
      const allowedCount = maxImages.value - allFiles.value.length;
      const newFiles = Array.from(target.files)
        .slice(0, allowedCount)
        .map((file) => ({
          file,
          preview: URL.createObjectURL(file),
          uploaded: false,
        }));
      allFiles.value.push(...newFiles);
      await uploadAll();
      await nextTick(checkScroll);
    }

    async function handleDrop(event: DragEvent) {
      isDragging.value = false;
      if (!event.dataTransfer?.files) return;
      const allowedCount = maxImages.value - allFiles.value.length;
      const newFiles = Array.from(event.dataTransfer.files)
        .slice(0, allowedCount)
        .map((file) => ({
          file,
          preview: URL.createObjectURL(file),
          uploaded: false,
        }));
      allFiles.value.push(...newFiles);
      await uploadAll();
      await nextTick(checkScroll);
    }

    function handleDragLeave(event: DragEvent) {
      if (!event.relatedTarget || !(event.currentTarget as Node).contains(event.relatedTarget as Node)) {
        isDragging.value = false;
      }
    }
function removeFile(index: number) {
  const removedFile = allFiles.value[index];

  // Remove from local files
  allFiles.value.splice(index, 1);

  // Emit event to parent with the index of removed file
  emit("media-removed", index);

  // Optionally, re-upload remaining files
  uploadAll();
  nextTick(checkScroll);
}


   async function uploadAll() {
  if (!allFiles.value.length) return;

  // Only take files that are not yet uploaded
  const filesToSend = allFiles.value.filter(f => f.file && !f.uploaded).map(f => f.file as File);
  if (!filesToSend.length) return;

  try {
    const result = await MediaService.uploadMedia(filesToSend, props.mediaLabel);

    if (result.status && result.data) {
      const files = Array.isArray(result.data) ? result.data : [result.data];
      toast.success("Files uploaded successfully");

      // Mark the files as uploaded (optional, you could also remove them if you want)
      allFiles.value = allFiles.value.map(f =>
        f.file && !f.uploaded
          ? { ...f, uploaded: true } // mark uploaded
          : f
      );

      // Emit the uploaded files so parent can handle them
      console.log(files)
      emit("media-uploaded", files);

    } else {
      toast.error(result.message || "Upload failed");
    }
  } catch (error: any) {
    toast.error(error.message || "An error occurred during upload");
  }
}

 function replaceFile(index: number) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";

  input.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (!target.files || !target.files[0]) return;

    const file = target.files[0];

    // Replace the file locally
    allFiles.value.splice(index, 1, {
      file,
      preview: URL.createObjectURL(file),
      uploaded: false,
    });

    // Upload the new image
    try {
      const result = await MediaService.uploadMedia([file], props.mediaLabel);

      if (result.status && result.data) {
        const uploaded = Array.isArray(result.data) ? result.data[0] : result.data;
        toast.success("Image replaced successfully");

        // Emit with index info
        emit("media-uploaded", [uploaded], index);
      } else {
        toast.error(result.message || "Replace failed");
      }
    } catch (error: any) {
      toast.error(error.message || "Error during replace");
    }

    await nextTick(checkScroll);
  };

  input.click();
}


    onMounted(() => {
      if (props.mediaUrls.length) {
        allFiles.value = props.mediaUrls.map((img: any) => ({
          file: null,
          preview: img.path.startsWith("http") ? img.path : `${img.path}`,
          uploaded: true,
          order: img.order,
        }));
      }
      nextTick(checkScroll);
    });

    watch(allFiles, async () => nextTick(checkScroll));

    return {
      allFiles,
      isDragging,
      isRTL,
      canSelectMore,
      canShowArrow,
      handleFileSelection,
      handleDrop,
      handleDragLeave,
      removeFile,
      uploadAll,
      replaceFile,
      scrollContainer,
      scrollNext,
      checkScroll,
      canScroll,
    };
  },
});


</script>

<style scoped>
.scrollbar-none::-webkit-scrollbar {
  display: none;
}
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
